import React, { FC, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Autocomplete,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { differenceInHours } from 'date-fns';
import { debounce, isEqual } from 'lodash';
import { toast } from 'react-toastify';
import { DateTimeRangePicker } from 'components/DateTimeRangePickerLocal/DateTimeRangePickerLocal';
import TextInput from 'components/TextInput/TextInput';
import { findErrorMessage } from 'helpers';
import { searchMerchants } from 'helpers/api';
import { formatLocalDateTime } from 'helpers/DateHelpers';
import { isErrorResponse } from 'helpers/typeGuards';
import { useEditBoostedOfferCampaignMutation } from 'reduxState/store/boostedOffers/api';
import { BoostedOffer, FundingSource } from 'reduxState/store/boostedOffers/types';
import { FUNDING_SOURCE_OPTIONS, SELECTABLE_FUNDING_SOURCES } from '../constants';

// valid rate must contains only numbers, and may include a decimal point
const isValidRate = (str: string) => /^\d*(\.\d+)?$/.test(str) && /\d/.test(str);

interface DropdownOption {
  label: string;
  id: string;
}

interface CampaignEditData {
  ID: number;
  ApplicationID: number;
  MerchantID: number;
  Rate: number;
  StartDate: string;
  EndDate: string;
  ModifiedAuthor: string;
  Kind: string;
  Note: string;
  FundingSource: FundingSource;
}

interface EditFormErrors {
  merchants: string;
  rate: string;
  startDate: string;
  endDate: string;
  note: string;
  kind: string;
  fundingSource: string;
}

export interface EditBoostedOfferCampaignFormProps {
  campaignToEdit: BoostedOffer;
  setCampaignToEdit: (campaign: BoostedOffer | null) => void;
  editFormRef: React.RefObject<HTMLDivElement>;
  isEditingActiveCampaign: boolean;
}

/**
 * EditBoostedOfferCampaignForm is a React component that allows users to edit the start/end date for boosted offer campaigns,
 * as well as the merchant associated with the campaign. Users can not edit the application associated with a campaign,
 * and cannot set start date or end date to a time in the past.
 *
 * @param campaignToEdit - Data corresponding to the campaign being edited
 * @param setCampaignToEdit - callback to set the campaign being edited. Can be set to null if no campaign is being edited.
 *
 * @component
 * @example
 * return (
 *   <EditBoostedOfferCampaignForm campaignToEdit={campaign} setCampaignToEdit={setCampaignToEdit} />
 * )
 */
const EditBoostedOfferCampaignForm: FC<EditBoostedOfferCampaignFormProps> = ({
  campaignToEdit,
  setCampaignToEdit,
  editFormRef,
  isEditingActiveCampaign,
}) => {
  const {
    ApplicationID,
    ApplicationName,
    MerchantID,
    MerchantName,
    Rate,
    StartDate,
    EndDate,
    Kind,
    FundingSource: initialFundingSource,
  } = campaignToEdit;
  const { user } = useAuth0();
  const applicationOption: DropdownOption = {
    label: ApplicationName,
    id: ApplicationID.toString(),
  };
  const [merchantOptions, setMerchantOptions] = useState<DropdownOption[]>([]);
  const [selectedMerchant, setSelectedMerchant] = useState<DropdownOption | null>({
    label: MerchantName,
    id: MerchantID.toString(),
  });
  const [rate, setRate] = useState(Rate);
  const [startDate, setStartDate] = useState<Date>(new Date(StartDate));
  const [endDate, setEndDate] = useState<Date>(new Date(EndDate));
  const [note, setNote] = useState('');
  const [selectedKind, setSelectedKind] = useState(Kind);
  const [fundingSource, setFundingSource] = useState(initialFundingSource);
  const [isMerchantsLoading, setIsMerchantsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState<EditFormErrors>({
    merchants: '',
    rate: '',
    startDate: '',
    endDate: '',
    note: '',
    kind: '',
    fundingSource: '',
  });
  const [editBoostedOfferCampaign, { isLoading: isEditReqLoading }] = useEditBoostedOfferCampaignMutation();

  const endsWithinThreeHrs = Math.abs(differenceInHours(endDate, new Date())) < 3;

  const handleMerchantSearch = async (query: string) => {
    if (query.length < 3) {
      return setMerchantOptions([]);
    }

    setIsMerchantsLoading(true);
    const results = await searchMerchants(query);

    if (isErrorResponse(results)) {
      return setError(findErrorMessage(results));
    }

    const merchantResults = results.filter(result => result.Type === 'merchant' && !result.Disabled);
    const merchantDropdownOptions: DropdownOption[] = merchantResults.map(({ ID, Value }) => ({
      id: String(ID),
      label: Value,
    }));

    setMerchantOptions(merchantDropdownOptions);
    setIsMerchantsLoading(false);
  };

  const debouncedMerchantSearch = debounce(handleMerchantSearch, 300);

  const handleOnSubmit = async () => {
    const newFormErrors: Partial<EditFormErrors> = {};

    if (!rate || !isValidRate(rate)) {
      newFormErrors.rate = 'Invalid Rate provided. Rate must be a number, and may include a decimal value.';
    }

    if (!startDate) {
      newFormErrors.startDate = 'Campaign must have a Start Date.';
    } else if (!isEditingActiveCampaign && startDate < new Date(Date.now())) {
      newFormErrors.startDate = 'Start Date for the campaign being edited can not come before today.';
    }

    if (!endDate) {
      newFormErrors.endDate = 'Campaign must have an End Date.';
    } else if (isEditingActiveCampaign && endsWithinThreeHrs) {
      newFormErrors.endDate = 'End Date must be at least three hours from now.';
    } else if (!(startDate < endDate)) {
      newFormErrors.endDate = 'End Date for the campaign being edited must be after Start Date.';
    }

    if (!selectedMerchant) {
      newFormErrors.merchants = 'A merchant must be provided to correspond with the campaign.';
    }

    if (!selectedKind) {
      newFormErrors.kind = 'Kind must be provided.';
    }

    if (!note.trim()) {
      newFormErrors.note = 'Note is required when editing boosted offer.';
    }

    if (!isEditingActiveCampaign && !SELECTABLE_FUNDING_SOURCES.includes(fundingSource)) {
      newFormErrors.fundingSource = 'Funding source must be provided.';
    }

    if (!isEqual(newFormErrors, {})) {
      return setFormErrors({ ...formErrors, ...newFormErrors });
    }

    const editedCampaign: CampaignEditData = {
      ID: campaignToEdit.ID,
      ApplicationID: Number(ApplicationID),
      MerchantID: Number(selectedMerchant!.id),
      Rate: Number(rate),
      StartDate: startDate.toISOString(),
      EndDate: endDate.toISOString(),
      ModifiedAuthor: user!.email!,
      Kind: selectedKind,
      Note: note,
      FundingSource: fundingSource,
    };

    setError('');

    try {
      await editBoostedOfferCampaign({
        campaign: editedCampaign,
      }).unwrap();
      toast.success('Campaign edited successfully!');
      setCampaignToEdit(null);
    } catch (errorResponse) {
      let errorMessage = 'Failed to edit boosted offer campaign.';
      const error = errorResponse.data;
      if (error?.ErrorMessage) {
        errorMessage = error.ErrorMessage[0].toUpperCase() + error.ErrorMessage.slice(1);
      }
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  return (
    <div ref={editFormRef} className="flex flex-col items-center flex-1 mt-12">
      <div className="max-w-[1400px] w-full h-fit flex flex-col font-montserrat bg-white rounded-md p-6 shadow-lg">
        <h4 className="w-full mb-2.5">Edit Boosted Offer Campaign</h4>
        <div className="flex flex-col w-full">
          <Autocomplete
            className="my-1"
            id="applications"
            options={[applicationOption]}
            multiple
            disabled
            value={[applicationOption]}
            filterSelectedOptions
            renderInput={params => <TextField {...params} label="Applications" placeholder="Select all applications" />}
          />
          <Autocomplete
            className="my-1"
            id="merchants"
            disabled={isEditingActiveCampaign}
            value={selectedMerchant}
            options={[...merchantOptions]}
            loading={isMerchantsLoading}
            isOptionEqualToValue={(option, value) => Boolean(option && value && option.id === value.id)}
            filterOptions={x => x}
            renderInput={params => (
              <TextField
                {...params}
                label="Merchant"
                placeholder="Select merchant"
                error={Boolean(formErrors.merchants)}
                helperText={formErrors.merchants}
              />
            )}
            onInputChange={(e, value) => debouncedMerchantSearch(value)}
            onChange={(e, value) => {
              setSelectedMerchant(value);
              setFormErrors({ ...formErrors, merchants: '' });
            }}
          />
          <div className="flex flex-row gap-2 items-center flex-wrap md:flex-nowrap">
            <FormControl error={Boolean(formErrors.kind)} fullWidth className="my-1 w-full md:w-[50%]">
              <InputLabel className="pl-1 pr-2 bg-white " id="kind-label">
                Kind
              </InputLabel>
              <Select
                notched
                variant="outlined"
                labelId="kind-label"
                value={selectedKind}
                disabled={isEditingActiveCampaign}
                defaultValue=""
                inputProps={{ 'data-testid': 'kind-select' }}
                onChange={e => {
                  setSelectedKind(e.target.value as string);
                  setFormErrors({ ...formErrors, kind: '' });
                }}
              >
                <MenuItem value="MULTIPLIER">Multiplier</MenuItem>
                <MenuItem value="PERCENTAGE">Percentage</MenuItem>
              </Select>
              {Boolean(formErrors.kind) && <FormHelperText>{formErrors.kind}</FormHelperText>}
            </FormControl>
            <div className="w-full md:w-[50%]">
              <TextInput
                label="Rate"
                value={rate}
                handleChange={val => {
                  setRate(val);
                  setFormErrors({ ...formErrors, rate: '' });
                }}
                hasError={Boolean(formErrors.rate)}
                errorText={formErrors.rate}
                isDisabled={isEditingActiveCampaign}
              />
            </div>
          </div>
          <FormControl error={Boolean(formErrors.fundingSource)} fullWidth className="my-[8px]">
            <InputLabel className="pl-1 pr-2 bg-white " id="funding-source-label">
              Funded By
            </InputLabel>
            <Select
              notched
              variant="outlined"
              labelId="funding-source-label"
              value={fundingSource}
              disabled={isEditingActiveCampaign}
              defaultValue=""
              inputProps={{ 'data-testid': 'funding-source-select' }}
              onChange={e => {
                setFundingSource(e.target.value as FundingSource);
                setFormErrors({ ...formErrors, fundingSource: '' });
              }}
            >
              {FUNDING_SOURCE_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            {Boolean(formErrors.fundingSource) && <FormHelperText>{formErrors.fundingSource}</FormHelperText>}
            {isEditingActiveCampaign && <FormHelperText>Cannot be changed on a running campaign</FormHelperText>}
          </FormControl>
          <div className="flex flex-col gap-2 lg:flex-row">
            <div className="flex flex-col">
              <DateTimeRangePicker
                startDateDisabled={isEditingActiveCampaign}
                endDateDisabled={endsWithinThreeHrs && !isEditingActiveCampaign}
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={value => {
                  setStartDate(value);
                  setFormErrors({ ...formErrors, startDate: '' });
                }}
                onEndDateChange={value => {
                  setEndDate(value);
                  setFormErrors({ ...formErrors, endDate: '' });
                }}
                startDateError={Boolean(formErrors.startDate)}
                endDateError={Boolean(formErrors.endDate)}
                startDateHelperText={formErrors.startDate}
                endDateHelperText={formErrors.endDate}
              />
              <div className="flex flex-row gap-2 mt-2">
                {startDate && (
                  <div className="w-[230px]">
                    <p className="mt-2 text-xs italic">
                      Pacific: {formatLocalDateTime(startDate, 'M/d/yyyy haaa', 'America/Los_Angeles')}
                    </p>
                    <p className="text-xs italic">
                      Mountain: {formatLocalDateTime(startDate, 'M/d/yyyy haaa', 'America/Denver')}
                    </p>
                    <p className="text-xs italic">
                      Central: {formatLocalDateTime(startDate, 'M/d/yyyy haaa', 'America/Chicago')}
                    </p>
                    <p className="text-xs italic">
                      Eastern: {formatLocalDateTime(startDate, 'M/d/yyyy haaa', 'America/New_York')}
                    </p>
                  </div>
                )}
                {endDate && (
                  <div className="w-[230px]">
                    <p className="mt-2 text-xs italic">
                      Pacific: {formatLocalDateTime(endDate, 'M/d/yyyy haaa', 'America/Los_Angeles')}
                    </p>
                    <p className="text-xs italic">
                      Mountain: {formatLocalDateTime(endDate, 'M/d/yyyy haaa', 'America/Denver')}
                    </p>
                    <p className="text-xs italic">
                      Central: {formatLocalDateTime(endDate, 'M/d/yyyy haaa', 'America/Chicago')}
                    </p>
                    <p className="text-xs italic">
                      Eastern: {formatLocalDateTime(endDate, 'M/d/yyyy haaa', 'America/New_York')}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-grow mt-2">
              <TextField
                label="Reason for change (Required)"
                fullWidth
                value={note}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setNote(event.target.value);
                  setFormErrors({ ...formErrors, note: '' });
                }}
                error={Boolean(formErrors.note)}
                helperText={formErrors.note}
              />
            </div>
          </div>
          <div className="flex items-center justify-end w-full h-20 mt-4">
            <div className="flex items-center justify-center w-full h-auto">
              {error && (
                <div>
                  <p className="text-red">{error}</p>
                </div>
              )}
            </div>
            <Button
              className="min-h-[50px] max-h-[50px] px-5 mr-2.5 bg-light-grey text-dark-grey font-semibold hover:bg-dark-purple hover:text-white"
              onClick={() => setCampaignToEdit(null)}
            >
              CANCEL
            </Button>
            <Button
              className={`min-h-[50px] max-h-[50px] px-5 font-semibold leading-snug ${
                isEditReqLoading
                  ? 'bg-light-grey2 text-light-purple'
                  : 'bg-light-purple text-white hover:bg-dark-purple'
              }`}
              disabled={isEditReqLoading}
              onClick={handleOnSubmit}
            >
              {isEditReqLoading ? 'Editing Campaign...' : 'Edit Campaign'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditBoostedOfferCampaignForm;
